import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Search, 
  Filter, 
  Download, 
  TrendingUp, 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Eye,
  CreditCard,
  Banknote,
  Smartphone,
  Wallet,
  ChevronDown,
  FileText,
  BarChart3
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const SalesHistoryPage = () => {
  const { company } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0], // Today
    end: new Date().toISOString().split('T')[0]     // Today
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleDetails, setShowSaleDetails] = useState(false);

  useEffect(() => {
    if (company?.id) {
      loadSales();
    }
  }, [company, dateRange]);

  const loadSales = async () => {
    try {
      setLoading(true);
      
      const options = {
        startDate: `${dateRange.start}T00:00:00`,
        endDate: `${dateRange.end}T23:59:59`
      };
      
      const data = await dataHelpers.getSales(company.id, options);
      setSales(data || []);
    } catch (error) {
      console.error('Error loading sales:', error);
      toast.error('Erro ao carregar histórico de vendas');
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSales = sales.filter(sale => {
    const matchesSearch = 
      sale.students?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.students?.registration_number?.includes(searchTerm) ||
      sale.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPayment = !selectedPaymentMethod || 
      sale.payment_method === selectedPaymentMethod ||
      (selectedPaymentMethod === 'balance' && sale.payment_method === 'student_balance');
    
    return matchesSearch && matchesPayment;
  });

  const calculateStats = () => {
    const totalSales = filteredSales.length;
    const totalAmount = filteredSales.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0);
    
    const paymentMethods = {
      cash: filteredSales.filter(s => s.payment_method === 'cash').length,
      card: filteredSales.filter(s => s.payment_method === 'card').length,
      pix: filteredSales.filter(s => s.payment_method === 'pix').length,
      balance: filteredSales.filter(s => s.payment_method === 'balance' || s.payment_method === 'student_balance').length
    };

    const uniqueStudents = new Set(filteredSales.map(s => s.student_id).filter(Boolean)).size;

    return {
      totalSales,
      totalAmount,
      paymentMethods,
      uniqueStudents,
      averageTicket: totalSales > 0 ? totalAmount / totalSales : 0
    };
  };

  const stats = calculateStats();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash':
        return <Banknote className="w-4 h-4" />;
      case 'card':
        return <CreditCard className="w-4 h-4" />;
      case 'pix':
        return <Smartphone className="w-4 h-4" />;
      case 'balance':
        return <Wallet className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Dinheiro',
      card: 'Cartão',
      pix: 'PIX',
      balance: 'Saldo do Aluno',
      student_balance: 'Saldo do Aluno'
    };
    return methods[method] || method;
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      cash: 'text-green-600 bg-green-100',
      card: 'text-blue-600 bg-blue-100',
      pix: 'text-purple-600 bg-purple-100',
      balance: 'text-orange-600 bg-orange-100',
      student_balance: 'text-orange-600 bg-orange-100'
    };
    return colors[method] || 'text-gray-600 bg-gray-100';
  };

  const handleViewSaleDetails = (sale) => {
    setSelectedSale(sale);
    setShowSaleDetails(true);
  };

  const exportToCSV = () => {
    if (filteredSales.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    const headers = [
      'Data/Hora',
      'ID da Venda',
      'Aluno',
      'Matrícula',
      'Forma de Pagamento',
      'Valor Total',
      'Produtos',
      'Quantidade'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredSales.map(sale => [
        `"${formatDateTime(sale.created_at)}"`,
        `"${sale.id}"`,
        `"${sale.students?.name || 'Sem aluno'}"`,
        `"${sale.students?.registration_number || '-'}"`,
        `"${getPaymentMethodLabel(sale.payment_method)}"`,
        sale.total_amount,
        `"${sale.products || '-'}"`,
        sale.quantity || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vendas_${dateRange.start}_${dateRange.end}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Relatório exportado com sucesso!');
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" />
        </div>
      
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Histórico de Vendas</h1>
            <p className="text-gray-600">
              Acompanhe as vendas realizadas e análise financeira
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Vendas</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalSales}</p>
                <p className="text-sm text-gray-500">vendas realizadas</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Faturamento</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
                <p className="text-sm text-gray-500">valor total</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Alunos Únicos</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.uniqueStudents}</p>
                <p className="text-sm text-gray-500">compraram</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <BarChart3 className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ticket Médio</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.averageTicket)}</p>
                <p className="text-sm text-gray-500">por venda</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Payment Methods Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-green-100">
                <Banknote className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Dinheiro</p>
                <p className="text-lg font-semibold text-gray-900">{stats.paymentMethods.cash}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-blue-100">
                <CreditCard className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Cartão</p>
                <p className="text-lg font-semibold text-gray-900">{stats.paymentMethods.card}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-purple-100">
                <Smartphone className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">PIX</p>
                <p className="text-lg font-semibold text-gray-900">{stats.paymentMethods.pix}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full bg-orange-100">
                <Wallet className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Saldo</p>
                <p className="text-lg font-semibold text-gray-900">{stats.paymentMethods.balance}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="Buscar por aluno, matrícula ou ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                />
              </div>
              
              <div className="flex space-x-2">
                <div className="flex space-x-2">
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="w-auto"
                  />
                  <span className="self-center text-gray-500">até</span>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="w-auto"
                  />
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? 'bg-blue-50 border-blue-300' : ''}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtros
                </Button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forma de Pagamento
                  </label>
                  <select
                    value={selectedPaymentMethod}
                    onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todas as formas</option>
                    <option value="cash">Dinheiro</option>
                    <option value="card">Cartão</option>
                    <option value="pix">PIX</option>
                    <option value="balance">Saldo do aluno</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Sales List */}
        <Card>
          <div className="overflow-hidden">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data/Hora
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aluno
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Pagamento
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSales.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {searchTerm || selectedPaymentMethod
                          ? 'Nenhuma venda encontrada com os filtros aplicados'
                          : 'Nenhuma venda registrada no período selecionado'
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredSales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-gray-50">
                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDateTime(sale.created_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {sale.id.substring(0, 8)}...
                        </div>
                      </td>

                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {sale.students?.name || 'Venda sem aluno'}
                        </div>
                        {sale.students?.registration_number && (
                          <div className="text-xs text-gray-500">
                            Matrícula: {sale.students.registration_number}
                          </div>
                        )}
                      </td>

                      <td className="px-3 md:px-6 py-4 hidden sm:table-cell">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(sale.payment_method)}`}>
                          {getPaymentMethodIcon(sale.payment_method)}
                          <span className="ml-1">{getPaymentMethodLabel(sale.payment_method)}</span>
                        </span>
                      </td>

                      <td className="px-3 md:px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(sale.total_amount)}
                        </div>
                        {sale.quantity && (
                          <div className="text-xs text-gray-500">
                            {sale.quantity} {sale.quantity === 1 ? 'item' : 'itens'}
                          </div>
                        )}
                      </td>

                      <td className="px-3 md:px-6 py-4 text-center">
                        <button 
                          onClick={() => handleViewSaleDetails(sale)}
                          title="Ver detalhes"
                          className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Sale Details Modal */}
      {showSaleDetails && selectedSale && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-medium text-gray-900">Detalhes da Venda</h3>
              <button
                onClick={() => setShowSaleDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">ID da Venda</p>
                  <p className="text-gray-900">{selectedSale.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Data/Hora</p>
                  <p className="text-gray-900">{formatDateTime(selectedSale.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Aluno</p>
                  <p className="text-gray-900">{selectedSale.students?.name || 'Venda sem aluno'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Matrícula</p>
                  <p className="text-gray-900">{selectedSale.students?.registration_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Forma de Pagamento</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(selectedSale.payment_method)}`}>
                    {getPaymentMethodIcon(selectedSale.payment_method)}
                    <span className="ml-1">{getPaymentMethodLabel(selectedSale.payment_method)}</span>
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Valor Total</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedSale.total_amount)}</p>
                </div>
              </div>
              
              {selectedSale.products && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Produtos</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{selectedSale.products}</p>
                  </div>
                </div>
              )}
              
              {selectedSale.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Observações</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{selectedSale.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesHistoryPage;
